import { platforms } from '../../pages/overview/_config/platforms';
import { getSystemFlag } from '../../pages/overview/_config/system';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { OFFICIAL_WEBSITE } from '../../pages/overview/_config/constant';
import { getName } from '@home/utils/common';
import { getMediaChannelIcon, mediaChannelMap } from '@/pages/home/_config/mediaChannal';
import Default from '@/pages/home/assets/svg/dashboard/default.svg';
import type {
  IDimensionConfig,
  IDimensionContext,
  IProgressItem,
  ICountryItem,
  ISelectorItem,
  IPlatformItem,
  ICatalogSelectedItem,
} from './type';
import { DEFAULT_COUNTRY_CODES, DEFAULT_ICON_OPACITY } from './_costant';
import {
  RANGE_DATE,
  RANGE_TIME,
  RANGE_DAILY,
  RANGE_WEEKLY,
  RANGE_MONTHLY,
} from '@/pages/dashboard/components/NewFilter/FilterBase/_config';

/**
 * 格式化百分比显示
 * @param percentage - 百分比数值
 * @returns 格式化后的百分比字符串
 */
export const formatPercentage = (percentage: number): string => {
  if (percentage < 0.1 && percentage > 0) return '<0.1%';
  return `${percentage.toFixed(1)}%`;
};

// ==================== 维度处理器配置 ====================

export const dimensionHandlers: Record<string, IDimensionConfig> = {
  // 国家维度处理器
  country: {
    getDefaultCodes: (defaultSelectedList, context) => {
      // 1. 优先使用筛选器中选中的国家（如果存在且不为空）
      if (context?.filterRegions && context.filterRegions.length > 0) {
        return context.filterRegions;
      }
      // 2. 其次使用 defaultSelectedList（如果存在）
      if (defaultSelectedList?.length) {
        return defaultSelectedList;
      }
      // 3. 最后使用默认国家列表
      return DEFAULT_COUNTRY_CODES;
    },

    createProgressItem: (code, { countryList, isEN }) => {
      const countryItem = countryList?.find((c: ICountryItem) => c?.country_code === code);
      const name = getName(isEN, countryItem?.country_name_en, countryItem?.country_name_ch);
      const flag = getCountryFlag(code);

      return {
        isOpacity: false,
        isBlackImg: false,
        disableIcon: flag,
        title: name || code,
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name: code,
      };
    },

    updateProgressItem: (item, { countryList, isEN }) => {
      const flag = getCountryFlag(item?.name || '');
      const countryItem = countryList?.find((c: ICountryItem) => c?.country_code === item?.name);
      const displayName = getName(isEN, countryItem?.country_name_en, countryItem?.country_name_ch);

      return {
        ...item,
        icon: flag,
        disableIcon: flag,
        title: displayName || item?.title || item?.name,
      };
    },
  },

  // 广告格式维度处理器
  ad_format: {
    getDefaultCodes: (defaultSelectedList, context) => {
      // 1. 优先使用筛选器中选中的 UA 渠道（如果存在且不为空）
      if (context?.filterAdFormat && context.filterAdFormat.length > 0) {
        return context.filterAdFormat;
      }
      // 2. 其次使用 defaultSelectedList（如果存在）
      if (defaultSelectedList?.length) {
        return defaultSelectedList;
      }
      // 3. 最后从 ua_network 列表中取前5个作为默认值（过滤掉 255）
      const adFormatList = context?.selector?.ad_format || [];
      return adFormatList
        .filter((item: ISelectorItem) => item?.code !== '255')
        .slice(0, 5)
        .map((item: ISelectorItem) => item?.code);
    },

    createProgressItem: (code, { selector, isEN }) => {
      const adFormatList = selector?.ad_format || [];
      const adFormatItem = adFormatList?.find((item: ISelectorItem) => item?.code === code);
      const name = getName(isEN, adFormatItem?.name_en, adFormatItem?.name_ch);
      const flag = getSystemFlag(code);

      return {
        isOpacity: DEFAULT_ICON_OPACITY,
        isBlackImg: false,
        disableIcon: flag,
        title: name || code,
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name: code,
      };
    },

    updateProgressItem: (item, { selector, isEN }) => {
      const adFormatList = selector?.ad_format || [];
      const adFormatItem = adFormatList?.find((ad: ISelectorItem) => ad?.code === item?.name);
      const displayName = getName(isEN, adFormatItem?.name_en, adFormatItem?.name_ch);

      return {
        ...item,
        title: displayName || item?.title || item?.name,
      };
    },
  },

  ua_network: {
    getDefaultCodes: (defaultSelectedList, context) => {
      // 1. 优先使用筛选器中选中的 UA 渠道（如果存在且不为空）
      if (context?.filterUaNetworks && context.filterUaNetworks.length > 0) {
        return context.filterUaNetworks;
      }
      // 2. 其次使用 defaultSelectedList（如果存在）
      if (defaultSelectedList?.length) {
        return defaultSelectedList;
      }
      // 3. 最后从 ua_network 列表中取前5个作为默认值（过滤掉 255）
      const uaNetworkList = context?.selector?.ua_network || [];
      return uaNetworkList
        .filter((item: ISelectorItem) => item?.code !== '255')
        .slice(0, 5)
        .map((item: ISelectorItem) => item?.code);
    },

    createProgressItem: (code, { selector, isEN }) => {
      const uaNetworkFormatList = selector?.ua_network || [];
      const uaNetworkFormatItem = uaNetworkFormatList?.find((item: ISelectorItem) => item?.code === code);
      const name = getName(isEN, uaNetworkFormatItem?.name_en, uaNetworkFormatItem?.name_ch);
      const channelIcon = uaNetworkFormatItem?.icon || getMediaChannelIcon(code || '');
      const resolvedIcon = channelIcon === mediaChannelMap._default ? Default : channelIcon;

      return {
        isOpacity: 0.2,
        isBlackImg: false,
        disableIcon: resolvedIcon,
        title: name || code,
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name: code,
      };
    },

    updateProgressItem: (item, { selector, isEN }) => {
      const uaNetworkList = selector?.ua_network || [];
      const uaNetworkItem = uaNetworkList?.find((ad: ISelectorItem) => ad?.code === item?.name);
      const displayName = getName(isEN, uaNetworkItem?.name_en, uaNetworkItem?.name_ch);
      const channelIcon = uaNetworkItem?.icon || getMediaChannelIcon(item?.name || '');
      const resolvedIcon = channelIcon === mediaChannelMap._default ? Default : channelIcon;

      return {
        ...item,
        icon: resolvedIcon,
        disableIcon: resolvedIcon,
        title: displayName || item?.title || item?.name,
      };
    },
  },

  // catalog（游戏）维度处理器
  catalog: {
    getDefaultCodes: (_defaultSelectedList, context) => {
      const catalogList = context?.filterCatalog?.list || [];
      return catalogList.slice(0, 5).map((item: ICatalogSelectedItem) => item?.value);
    },

    createProgressItem: (code, { filterCatalog, isEN }) => {
      const catalogList = filterCatalog?.list || [];
      const catalogItem = catalogList.find((item: ICatalogSelectedItem) => item?.value === code);
      const name = getName(isEN, catalogItem?.nameEn, catalogItem?.nameCn) || code;

      return {
        isOpacity: false,
        isBlackImg: false,
        disableIcon: catalogItem?.image || '',
        icon: catalogItem?.image || '',
        title: name,
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name: code,
      };
    },

    updateProgressItem: (item, { filterCatalog, isEN }) => {
      const catalogList = filterCatalog?.list || [];
      // 支持多种匹配方式：name → value，title(full_name) → value/nameEn/nameCn
      const catalogItem = catalogList.find(
        (c: ICatalogSelectedItem) =>
          c?.value === item?.name
          || c?.value === item?.title
          || c?.nameEn === item?.title
          || c?.nameCn === item?.title
      );
      const displayName = getName(isEN, catalogItem?.nameEn, catalogItem?.nameCn);

      return {
        ...item,
        icon: catalogItem?.image || item?.icon || item?.disableIcon,
        disableIcon: catalogItem?.image || item?.disableIcon,
        title: displayName || item?.title || item?.name,
      };
    },
  },

  // OS/平台维度处理器
  os: {
    getDefaultCodes: () => [],
    createProgressItem: (): IProgressItem => {
      const emptyItem: IProgressItem = {
        isOpacity: false,
        isBlackImg: false,
        disableIcon: '',
        title: '',
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name: '',
      };
      return emptyItem;
    },
    updateProgressItem: (item) => item,
  },
};

// ==================== 工具函数 ====================

/**
 * 创建 OS 维度的进度列表
 * @param defaultSelectedList - 默认选中的平台 ID 列表
 * @param context - 维度上下文
 * @returns 进度条项列表
 */
export const createOSProgressList = (
  defaultSelectedList: string[] | undefined,
  context: IDimensionContext
): IProgressItem[] => {
  const { systemId, platformList, gameCode } = context;
  const selectPlatformList = systemId?.includes('255')
    ? platformList
    : platformList?.filter((item) => systemId?.includes(item?.os_id));

  let newPlatformList: IPlatformItem[];
  if (defaultSelectedList?.length) {
    newPlatformList = defaultSelectedList
      .map((osId: string) => selectPlatformList?.find((item) => item?.os_id === osId))
      .filter((item): item is IPlatformItem => Boolean(item));
  } else {
    newPlatformList = selectPlatformList?.length > 5
      ? selectPlatformList?.slice(0, 5)
      : selectPlatformList || [];
  }

  if (!newPlatformList?.length && defaultSelectedList?.length) {
    return defaultSelectedList.map((name) => {
      const resolved = resolvePlatformIconByName(name);
      return {
        isOpacity: resolved.isOpacity,
        isBlackImg: resolved.isBlack,
        disableIcon: resolved.disableIcon,
        title: resolved.title,
        hasData: false,
        isSelected: true,
        num: '-',
        share: '',
        name,
      };
    });
  }

  return newPlatformList?.map((item: IPlatformItem) => {
    const name = item?.os_name_en?.trim()?.toLowerCase();
    let systemName = name;

    if (name && OFFICIAL_WEBSITE.includes(name) && gameCode) {
      systemName = `${gameCode?.toLowerCase()?.trim()}_${name}`;
    }

    const dataItem = platforms.find(
      (platformItem) =>
        platformItem?.title?.trim()?.toLowerCase() === systemName ||
        platformItem?.subtitle?.find((w) => name?.startsWith(w))
    );
    const disableIcon = dataItem ? dataItem?.disableIcon : getSystemFlag(name || '');

    return {
      isOpacity: !dataItem || dataItem?.isOpacity || false,
      isBlackImg: dataItem?.isBlack || false,
      disableIcon,
      title: item?.os_name_en || '',
      hasData: false,
      isSelected: true,
      num: '-',
      share: '',
      name: item?.os_id || item?.os_name_en || '',
    };
  });
};

/**
 * 根据维度类型创建进度列表
 * @param dimension - 维度类型
 * @param defaultSelectedList - 默认选中列表
 * @param context - 维度上下文
 * @returns 进度条项列表
 */
export const createProgressListByDimension = (
  dimension: string,
  defaultSelectedList: string[] | undefined,
  context: IDimensionContext
): IProgressItem[] => {
  const handler = dimensionHandlers[dimension];

  if (!handler || dimension === 'os') {
    return createOSProgressList(defaultSelectedList, context);
  }

  const codes = handler.getDefaultCodes(defaultSelectedList, context);
  return codes.map((code) => handler.createProgressItem(code, context));
};

/**
 * 更新进度列表的显示信息
 * @param dimension - 维度类型
 * @param progressList - 进度条列表
 * @param context - 维度上下文
 * @returns 更新后的进度条列表
 */
export const updateProgressListByDimension = (
  dimension: string,
  progressList: IProgressItem[],
  context: IDimensionContext
): IProgressItem[] => {
  const handler = dimensionHandlers[dimension];

  if (!handler?.updateProgressItem) {
    return progressList;
  }

  return progressList?.map((item) => handler.updateProgressItem!(item, context));
};

/**
 * 根据平台名称解析图标（复用 platforms 配置和 getSystemFlag）
 * @param name - 平台名称（如 'steam'、'xbox'）
 * @returns { icon, disableIcon, isOpacity, isBlack }
 */
export const resolvePlatformIconByName = (
  name: string
): { icon: string; disableIcon: string; isOpacity: boolean; isBlack: boolean; title: string; } => {
  const normalizedName = name?.trim()?.toLowerCase();
  const dataItem = platforms.find(
    (item) =>
      item?.title?.trim()?.toLowerCase() === normalizedName ||
      item?.subtitle?.find((w: string) => normalizedName?.startsWith(w))
  );
  const fallbackIcon = getSystemFlag(normalizedName || '');
  return {
    icon: dataItem?.icon || fallbackIcon,
    disableIcon: dataItem?.disableIcon || fallbackIcon,
    isOpacity: !dataItem || dataItem?.isOpacity || false,
    isBlack: dataItem?.isBlack || false,
    title: dataItem?.title || name,
  };
};

// ==================== Studio → 经分下钻 filterParams ====================

/** Studio date_type 与经分 timeType 对齐（经分侧统一为 Day / Week / Month） */
export const DATE_TYPE_TO_TIME_TYPE: Record<string, string> = {
  daily: 'Day',
  weekly: 'Week',
  monthly: 'Month',
};

/** 与 PcConsoleDaily/Weekly/MonthlyFilter 的 filterInit 一致，供 NewFilter#getDefaultValueFromUrl */
const DATE_TYPE_TO_RANGE_DATE_KEY: Record<string, string> = {
  daily: RANGE_DAILY,
  weekly: RANGE_WEEKLY,
  monthly: RANGE_MONTHLY,
};

export type TJumpDetailFormPayload = {
  filterValue: Record<string, unknown>;
  timeType: string;
  rangeDateKey: string;
  /** 与原先 PlatformCard / jumpDetail 一致：为 true 时不写入 history.state.filterParams */
  diff?: boolean;
};

/**
 * 将 Studio 筛选（start_time / end_time / date_type）转成经分 jumpDetail 的 filterParams，
 * 与 NewFilter#getDefaultValueFromUrl 下钻继承字段对齐。
 */
export function buildJumpDetailFormValue(
  rawFilter: Record<string, unknown>,
  resolvedTimeType: string,
): TJumpDetailFormPayload {
  const fv = { ...rawFilter };
  // 与 MetricCard 一致：diff 只放在 jumpDetail 入参顶层；jumpDetail 在 diff 为 true 时不附带 filterParams
  const diffForJump = typeof fv.diff === 'boolean' ? fv.diff : undefined;
  delete fv.diff;
  const start = fv.start_time;
  const end = fv.end_time;
  const dateType = String(fv.date_type || 'daily');
  const allowedRangeKeys = new Set([RANGE_DAILY, RANGE_WEEKLY, RANGE_MONTHLY]);
  const fromContext =
    typeof fv.rangeDateKey === 'string' && allowedRangeKeys.has(fv.rangeDateKey)
      ? fv.rangeDateKey
      : '';
  const rangeDateKey = fromContext || DATE_TYPE_TO_RANGE_DATE_KEY[dateType] || RANGE_DAILY;

  if (start !== null && start !== undefined && end !== null && end !== undefined && start !== '' && end !== '') {
    const range: [string, string] = [String(start), String(end)];
    fv[RANGE_TIME] = range;
    fv[RANGE_DATE] = range;
    if (dateType === 'daily') {
      fv[RANGE_DAILY] = range;
    } else if (dateType === 'weekly') {
      fv[RANGE_WEEKLY] = range;
    } else if (dateType === 'monthly') {
      fv[RANGE_MONTHLY] = range;
    }
  }

  return {
    filterValue: fv,
    timeType: resolvedTimeType,
    rangeDateKey,
    ...(diffForJump !== undefined ? { diff: diffForJump } : {}),
  };
}

